import ActionAutoConfirmCountdown from './components/ActionAutoConfirmCountdown';
import { AlertCircle, ChevronRight, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatValue } from '@/lib/formatters';
import { getUserActionMeta } from '../../model/userActionMeta';
import { KeyboardEvent } from 'react';
import { UserActionCardProps } from './types';
import { UserActionResponse } from '../../api';
import { UserActionTone } from '../../model/types';

const userActionIconByTone: Record<UserActionTone, typeof AlertCircle> = {
  orange: TriangleAlert,
  red: AlertCircle
};

const userActionIconColorByTone: Record<UserActionTone, string> = {
  orange: 'text-warning',
  red: 'text-destructive'
};

const getUserActionDescription = (action: UserActionResponse): string | null => {
  const meta = getUserActionMeta(action.code);

  if (meta.descriptionResolver) return meta.descriptionResolver(action);
  if (meta.description) return meta.description;

  return null;
};

const UserActionCard = (props: UserActionCardProps) => {
  const {
    actions,
    className,
    invoiceNumber,
    onAutoConfirmExpired,
    onClick,
    shipmentAutoConfirmAt,
    shipmentNumber,
    supplierName,
    taskCount,
    variant = 'main'
  } = props;

  const firstAction = actions[0];
  const isFooterVisible = variant === 'main';

  const onClickCard = (): void => {
    if (!firstAction) return;

    onClick?.(firstAction);
  };

  const onKeyDownCard = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;

    event.preventDefault();
    onClickCard();
  };

  return (
    <article
      className={cn(
        'flex w-full flex-col overflow-hidden group',
        'rounded-2xl border border-border-strong bg-surface',
        'transition-colors hover:bg-accent-soft active:bg-accent-soft',
        'cursor-pointer',
        className
      )}
      onClick={onClickCard}
      onKeyDown={onKeyDownCard}
      role="button"
      tabIndex={0}
    >
      <div
        className={cn(
          'flex flex-col px-4',
          variant === 'main' && 'pt-1'
        )}
      >
        {actions.map((action, index) => {
          const description = getUserActionDescription(action);
          const Icon = userActionIconByTone[getUserActionMeta(action.code).tone];
          const isShipmentReceptionDecisionAction =
            variant === 'shipment' && action.code === 'CONFIRM_RECEPTION_DECISION';

          return (
            <div
              className={cn(
                'flex flex-col gap-2 py-2',
                index < actions.length - 1 && 'border-b border-border-strong'
              )}
              key={action.id}
            >
              <div className="flex items-start gap-2">
                <Icon
                  className={cn(
                    'mt-0.5 size-4 shrink-0',
                    userActionIconColorByTone[getUserActionMeta(action.code).tone]
                  )}
                  strokeWidth={1.75}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <p className="font-sans text-sm leading-5 text-foreground">
                    {getUserActionMeta(action.code).title}
                  </p>
                  {description ? (
                    <p className="font-sans text-xs leading-4 text-muted-foreground-soft">
                      {description}
                    </p>
                  ) : null}
                </div>
                {variant === 'shipment' ? (
                  <ChevronRight
                    className="self-center size-4 shrink-0 text-muted-foreground-soft"
                    strokeWidth={1.75}
                  />
                ) : null}
              </div>
              {isShipmentReceptionDecisionAction && shipmentAutoConfirmAt ? (
                <ActionAutoConfirmCountdown
                  autoConfirmAt={shipmentAutoConfirmAt}
                  onAutoConfirmExpired={onAutoConfirmExpired}
                />
              ) : null}
            </div>
          );
        })}
      </div>
      {isFooterVisible ? (
        <div
          className={cn(
            'flex items-center gap-2.5 px-4 pb-3 pt-[9px]',
            'border-t border-border-strong bg-muted',
            'transition-colors group-hover:bg-accent-soft group-active:bg-accent-soft'
          )}
        >
          <div className="flex min-w-0 flex-1 flex-col">
            <p className="truncate font-sans text-xs font-semibold leading-4 text-muted-foreground">
              {formatValue({ value: shipmentNumber ?? invoiceNumber })}
            </p>
            <p className="truncate font-sans text-xs leading-4 text-muted-foreground-soft">
              {formatValue({ value: supplierName })}
            </p>
          </div>
          {taskCount ? (
            <span className="font-sans text-xs leading-4 text-muted-foreground-soft">{taskCount}</span>
          ) : null}
          <ChevronRight className="size-4 shrink-0 text-muted-foreground-soft" strokeWidth={1.75} />
        </div>
      ) : null}
    </article>
  );
};

export default UserActionCard;